import { AnalyticsService } from 'base/analytics';
import { BlocksService } from 'blocks/blocks_service';
import { Project } from 'blocks/editor/model';
import { CollabSyncer } from 'blocks/sync/collab_syncer';
import { loadProject } from 'blocks/sync/sync';
import React from 'react';

/**
 * Connects to a project.
 **/
export function useProjectConnectionState(
  projectToken: string | undefined,
  blocksService: BlocksService,
  analyticsServiceNoContext: AnalyticsService,
): [
  isConnecting: boolean,
  connectionError: unknown,
  project: Project | undefined,
  analytics: AnalyticsService,
] {
  const [state, setState] = React.useState<
    | { connected: false; error?: unknown }
    | ({ connected: true } & Awaited<ReturnType<typeof loadProject>>)
  >({ connected: false });

  React.useEffect(() => {
    if (projectToken == null) {
      setState({ connected: false });
      return;
    }

    let aborted = false;
    let sync: CollabSyncer;
    loadProject({
      projectToken,
      blocksService,
      // Don't show the figma usage in the avatars list back in the editor
      hideFromPresence: true,
    })
      .then((v) => {
        if (aborted) {
          v.sync?.disconnect();
          return;
        }
        setState({ ...v, connected: true });
        sync = v.sync;
      })
      .catch((error) => {
        console.error('Error connecting to project', error);
        !aborted && setState({ connected: false, error });
      });
    return () => {
      aborted = true;
      sync?.disconnect();
    };
  }, [projectToken]);

  return [
    projectToken != null && !state.connected,
    state.connected ? state.sync?.fatalError : state.error,
    state.connected ? state.project : undefined,
    analyticsServiceNoContext.withContext(() =>
      state.connected ? state.analyticsContext : {},
    ),
  ];
}
